import { Box, Chip, Typography } from '@mui/material';
import { Datagrid, DateField, FunctionField, List, NumberField, useRecordContext } from 'react-admin';
import ListPagination from '../../components/ListPagination';
import { __t } from '../../i18n/t';
import { InternalWorkerRun, StationExclusion } from '../../models/attendantGoalOffer';
import InternalGate from '../attendantGoalOffers/InternalGate';
import { DATE_TIME, exclusionLabel, fmtDuration, workerStatusLabel } from '../attendantGoalOffers/labels';

const Excluded = () => {
  const r = useRecordContext<InternalWorkerRun>();
  const entries = (Object.entries(r?.excluded ?? {}) as Array<[StationExclusion, number]>).filter(([, n]) => Number(n) > 0);
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{__t('Postos excluídos da análise')}</Typography>
      {entries.length
        ? entries.map(([reason, n]) => <Typography key={reason} variant="body2">{exclusionLabel(reason)}{': '}{n}</Typography>)
        : <Typography variant="body2" color="text.secondary">{__t('Nenhum posto excluído.')}</Typography>}
    </Box>
  );
};

// Só `weekStart` e `startedAt` estão no RUN_SORT do backend; as demais colunas não ordenam.
const count = { sortable: false, emptyText: '—' } as const;

const AttendantGoalWorkerRunList = () => (
  <InternalGate>
    <List
      title="Execuções semanais da Meta de Frentista"
      perPage={25}
      pagination={<ListPagination />}
      exporter={false}
      empty={false}
      sort={{ field: 'weekStart', order: 'DESC' }}
    >
      {/* RaLayout-root é fit-content: com 14 colunas, a tabela rola aqui dentro em vez de esticar a página. */}
      <Box sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
        <Datagrid bulkActionButtons={false} expand={<Excluded />} isRowExpandable={() => true}>
          <DateField source="weekStart" label="Semana" locales="pt-BR" />
          <FunctionField<InternalWorkerRun> label="Status" sortable={false} render={r => workerStatusLabel(r.status)} />
          <FunctionField<InternalWorkerRun>
            label="Simulação"
            sortable={false}
            render={r => <Chip size="small" color={r.dryRun ? 'warning' : 'success'} label={r.dryRun ? __t('Sim') : __t('Não')} />}
          />
          <DateField source="startedAt" label="Início" showTime locales="pt-BR" options={DATE_TIME} sx={{ whiteSpace: 'nowrap' }} />
          <FunctionField<InternalWorkerRun> label="Duração" sortable={false} render={r => fmtDuration(r.durationSeconds)} />
          <NumberField source="chainsEvaluated" label="Redes avaliadas" {...count} />
          <NumberField source="stationsAnalyzed" label="Postos analisados" {...count} />
          <NumberField source="t1" label="T1" {...count} />
          <NumberField source="t2" label="T2" {...count} />
          <NumberField source="chainsWithTrigger" label="Redes com gatilho" {...count} />
          <NumberField source="offersCreated" label="Convites criados" {...count} />
          <NumberField source="offersRefreshed" label="Convites recalculados" {...count} />
          <NumberField source="offersClosedNoTrigger" label="Convites fechados" {...count} />
          <NumberField source="errors" label="Erros" {...count} />
        </Datagrid>
      </Box>
    </List>
  </InternalGate>
);

export default AttendantGoalWorkerRunList;
